import { useState } from "react";
import { Link } from "react-router-dom";

import { FormError, PublicShell } from "@/auth/PublicShell";
import { forgotPassword } from "@/auth/public_api";

/** "I forgot my password" — and a deliberate non-answer.
 *
 *  SEC-01. The screen after submit is the SAME screen whether or not that
 *  address has an account, because any difference is an account-existence
 *  oracle: type a list of addresses, keep the ones that say "sent". So the
 *  copy says what WOULD happen ("if that address has an account") rather than
 *  what did.
 *
 *  The wording is only half of it — Fabric equalises the response TIME on this
 *  path too, because a fast "no such user" leaks through the clock exactly
 *  what the sentence stopped leaking. This page must therefore never
 *  short-circuit: no client-side "we know that's not a real domain", no early
 *  return. It always awaits the call.
 */
export function ForgotPasswordPage() {
  const [loginId, setLoginId] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await forgotPassword(loginId);
      setSent(true);
    } catch {
      // Rate limiting is the only failure a person can actually hit here
      // (Fabric: 5 per hour). Everything else is deliberately indistinguishable.
      setError("Too many attempts - wait an hour and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <PublicShell
        eyebrow="Orbit Platform"
        title="Check your email"
        sub={<>If <strong>{loginId}</strong> has an account, a reset link is on its way. The link is valid for one hour and can be used once.</>}
      >
        <div className="cap" style={{ textAlign: "center", marginBottom: 14 }}>
          Nothing arrived? Check spam, then ask your administrator - the address may
          not be the one on the account.
        </div>
        <Link to="/" className="btn ghost" style={{ textAlign: "center" }}>Back to sign in</Link>
      </PublicShell>
    );
  }

  return (
    <PublicShell
      eyebrow="Orbit Platform"
      title="Reset your password"
      sub="Enter the email address on your account and we'll send you a link."
    >
      <form onSubmit={submit}>
        <div className="igroup">
          <label htmlFor="fp-e">Email</label>
          <input
            id="fp-e"
            className="inp"
            type="email"
            required
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            autoComplete="username"
            autoFocus
          />
        </div>
        {error && <FormError>{error}</FormError>}
        <button className="btn-signin" type="submit" disabled={busy || !loginId}>
          {busy ? "Sending…" : "Send reset link →"}
        </button>
      </form>
      <div style={{ textAlign: "center", marginTop: 14 }}>
        <Link to="/">Back to sign in</Link>
      </div>
    </PublicShell>
  );
}
